/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useQuizStore } from 'components/BuildVsBuyQuiz/hooks/useQuizStore';
import { calculateResultBucket } from 'components/BuildVsBuyQuiz/utils/scoring';

import { analyticsClient } from 'utils/analytics/client';

import type { AnswerType, QuizQuestion, QuizVariant, ResultBucket } from 'components/BuildVsBuyQuiz/config/types';
import type { ResultContentProps } from 'components/BuildVsBuyQuiz/utils/scoring';
import type { ButtonConfigProps } from 'molecules/Button/config';

interface UseQuizStateProps {
  variant: QuizVariant;
  quizVersion: string;
  redirectUrl: string;
  questions: QuizQuestion[];
  clearBuyHeading?: string;
  clearBuyDescription?: string;
  clearBuyButtons?: ButtonConfigProps[];
  leansBuyHeading?: string;
  leansBuyDescription?: string;
  leansBuyButtons?: ButtonConfigProps[];
  tossUpHeading?: string;
  tossUpDescription?: string;
  tossUpButtons?: ButtonConfigProps[];
  leansBuildHeading?: string;
  leansBuildDescription?: string;
  leansBuildButtons?: ButtonConfigProps[];
  clearBuildHeading?: string;
  clearBuildDescription?: string;
  clearBuildButtons?: ButtonConfigProps[];
}

interface UseQuizStateReturn {
  currentStep: number;
  answers: Record<number, AnswerType>;
  blueCount: number;
  orangeCount: number;
  resultBucket: ResultBucket;
  isTransitioning: boolean;
  isHydrating: boolean;
  isCompletingQuiz: boolean;
  handleAnswer: (questionIndex: number, answer: AnswerType) => void;
  handleBack: () => void;
}

export const useQuizState = ({
  variant,
  quizVersion,
  redirectUrl,
  questions,
  clearBuyHeading,
  clearBuyDescription,
  clearBuyButtons,
  leansBuyHeading,
  leansBuyDescription,
  leansBuyButtons,
  tossUpHeading,
  tossUpDescription,
  tossUpButtons,
  leansBuildHeading,
  leansBuildDescription,
  leansBuildButtons,
  clearBuildHeading,
  clearBuildDescription,
  clearBuildButtons,
}: UseQuizStateProps): UseQuizStateReturn => {
  const router = useRouter();
  const store = useQuizStore();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isHydrating, setIsHydrating] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [isCompletingQuiz, setIsCompletingQuiz] = useState(false);
  const hasInitialized = useRef(false);
  const hasResetForResult = useRef(false);

  const resetQuiz = useCallback(() => {
    store.reset();
    setCurrentStep(1);
    setIsHydrating(false);
    hasResetForResult.current = false;
    sessionStorage.removeItem('build-vs-buy-quiz-unmounted');
    localStorage.removeItem('build-vs-buy-quiz-storage');
  }, [store]);

  useEffect(
    () => () => {
      sessionStorage.setItem('build-vs-buy-quiz-unmounted', 'true');
    },
    [],
  );

  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }

    const wasUnmounted = sessionStorage.getItem('build-vs-buy-quiz-unmounted');
    if (wasUnmounted === 'true') {
      sessionStorage.removeItem('build-vs-buy-quiz-unmounted');
      resetQuiz();
      hasInitialized.current = true;

      return;
    }

    let pollCount = 0;
    const pollInterval = setInterval(() => {
      pollCount++;
      const hydrated = sessionStorage.getItem('build-vs-buy-quiz-hydrated');

      if (hydrated === 'true' || pollCount >= 50) {
        clearInterval(pollInterval);
        hasInitialized.current = true;
        sessionStorage.removeItem('build-vs-buy-quiz-hydrated');

        if (store.resultBucket) {
          resetQuiz();

          return;
        }

        const answerCount = Object.keys(store.answers).length;
        if (answerCount > 0) {
          const step = answerCount + 1;
          setCurrentStep(step);
          setIsHydrating(false);
        } else {
          setIsHydrating(false);
        }
      }
    }, 100);

    return () => clearInterval(pollInterval);
  }, [store, resetQuiz]);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (!url.includes('/build-vs-buy') && !url.includes('/results')) {
        localStorage.removeItem('build-vs-buy-quiz-storage');
        localStorage.removeItem('build-vs-buy-quiz-result-content');
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => router.events.off('routeChangeStart', handleRouteChange);
  }, [router]);

  useEffect(() => {
    if (isHydrating) {
      return;
    }
    if (store.variant !== variant) {
      store.setVariant(variant);
    }
    if (store.quizVersion !== quizVersion) {
      store.setQuizVersion(quizVersion);
    }
  }, [isHydrating, variant, quizVersion, store]);

  useEffect(() => {
    if (typeof window !== 'undefined' && variant === 'gated_start') {
      const savedEmail = sessionStorage.getItem('build-vs-buy-quiz-user-email');
      if (savedEmail && !store.email) {
        store.setEmail(savedEmail);
      } else if (!savedEmail) {
        console.error('[Quiz State] ❌ NO email in sessionStorage!');
      }
    }
  }, [variant, store]);

  useEffect(() => {
    if (!isHydrating && store.resultBucket && !hasResetForResult.current) {
      hasResetForResult.current = true;
      resetQuiz();
    }

    if (!store.resultBucket) {
      hasResetForResult.current = false;
    }
  }, [isHydrating, store.resultBucket, resetQuiz]);

  const trackEvent = useCallback(
    (eventName: string, properties: Record<string, any> = {}) => {
      if (typeof window === 'undefined') {
        return;
      }

      const payload = {
        quiz_variant: variant,
        quiz_version: quizVersion,
        ...properties,
      };

      const sendEvent = () => {
        if (!analyticsClient.isSegmentReady() && !analyticsClient.isRudderReady()) {
          return false;
        }

        analyticsClient.track(eventName, payload);

        return true;
      };

      if (sendEvent()) {
        return;
      }

      let retries = 0;
      const retryInterval = setInterval(() => {
        retries++;
        if (sendEvent() || retries >= 3) {
          clearInterval(retryInterval);
        }
      }, 500);
    },
    [variant, quizVersion],
  );

  const injectMarketoFields = useCallback(() => {
    if (typeof window === 'undefined' || !(window as any).MktoForms2) {
      return;
    }

    const forms = (window as any).MktoForms2.allForms();
    forms.forEach((form: any) => {
      const fields: Record<string, string> = {
        Build_vs_Buy_Quiz_Variant__c: variant,
        Build_vs_Buy_Quiz_Version__c: quizVersion,
        Build_vs_Buy_Quiz_Completed__c: Object.keys(store.answers).length === questions.length ? 'true' : 'false',
      };

      if (store.resultBucket) {
        fields.Build_vs_Buy_Quiz_Result_Bucket__c = store.resultBucket;
        fields.Build_vs_Buy_Quiz_Blue_Count__c = String(store.blueCount);
        fields.Build_vs_Buy_Quiz_Orange_Count__c = String(store.orangeCount);
      }

      form.addHiddenFields(fields);
    });
  }, [variant, quizVersion, store, questions.length]);

  const saveResultContentToLocalStorage = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const resultContent: ResultContentProps = {
      clearBuyHeading,
      clearBuyDescription,
      clearBuyButtons,
      leansBuyHeading,
      leansBuyDescription,
      leansBuyButtons,
      tossUpHeading,
      tossUpDescription,
      tossUpButtons,
      leansBuildHeading,
      leansBuildDescription,
      leansBuildButtons,
      clearBuildHeading,
      clearBuildDescription,
      clearBuildButtons,
    };

    const hasContent = Object.values(resultContent).some(v => v !== undefined);
    if (hasContent) {
      localStorage.setItem('build-vs-buy-quiz-result-content', JSON.stringify(resultContent));
    }
  }, [
    clearBuyHeading,
    clearBuyDescription,
    clearBuyButtons,
    leansBuyHeading,
    leansBuyDescription,
    leansBuyButtons,
    tossUpHeading,
    tossUpDescription,
    tossUpButtons,
    leansBuildHeading,
    leansBuildDescription,
    leansBuildButtons,
    clearBuildHeading,
    clearBuildDescription,
    clearBuildButtons,
  ]);

  const redirectToResults = useCallback(
    (resultBucket: ResultBucket) => {
      if (typeof window === 'undefined') {
        return;
      }

      const params = new URLSearchParams({
        variant,
        version: quizVersion,
        result: resultBucket,
        blue: String(store.blueCount),
        orange: String(store.orangeCount),
        email: store.email || '',
      });

      Object.entries(store.answers).forEach(([index, answer]) => {
        params.append(`q${Number(index) + 1}`, answer);
      });

      const finalUrl = `${redirectUrl}?${params.toString()}`;

      setTimeout(() => {
        window.location.replace(finalUrl);
      }, 100);
    },
    [redirectUrl, variant, quizVersion, store],
  );

  const handleAnswer = useCallback(
    (questionIndex: number, answer: AnswerType) => {
      const question = questions[questionIndex];
      const isLastQuestion = questionIndex === questions.length - 1;

      if (isLastQuestion) {
        setIsCompletingQuiz(true);
      }

      store.setAnswer(questionIndex, answer);

      trackEvent('build_vs_buy_quiz_step_answered', {
        step_number: questionIndex + 1,
        section: question.section,
        question_id: question.id,
        answer_type: answer,
      });

      setIsTransitioning(true);

      setTimeout(() => {
        if (isLastQuestion) {
          const resultBucket = calculateResultBucket(store.blueCount, store.orangeCount, questions.length);
          store.setResultBucket(resultBucket);

          if (!store.hasTrackedCompletion) {
            trackEvent('build_vs_buy_quiz_completed', {
              result_bucket: resultBucket,
              blue_count: store.blueCount,
              orange_count: store.orangeCount,
            });
            store.markCompletionTracked();
          }

          injectMarketoFields();
          saveResultContentToLocalStorage();

          setTimeout(() => {
            redirectToResults(resultBucket);
          }, 50);
        } else {
          const nextStep = currentStep + 1;
          setCurrentStep(nextStep);

          const nextQuestion = questions[questionIndex + 1];
          trackEvent('build_vs_buy_quiz_step_viewed', {
            step_number: nextStep,
            section: nextQuestion.section,
            question_id: nextQuestion.id,
          });
        }

        setTimeout(() => setIsTransitioning(false), 50);
      }, 300);
    },
    [
      currentStep,
      questions,
      store,
      trackEvent,
      injectMarketoFields,
      saveResultContentToLocalStorage,
      redirectToResults,
    ],
  );

  const handleBack = useCallback(() => {
    const newStep = Math.max(1, currentStep - 1);
    const questionIndex = newStep - 1;

    store.clearAnswersFrom(questionIndex);
    setCurrentStep(newStep);
  }, [currentStep, store]);

  useEffect(() => {
    if (isHydrating) {
      return;
    }

    if (!store.hasTrackedStart) {
      trackEvent('build_vs_buy_quiz_started', {
        capture_point: variant === 'gated_start' ? 'start' : 'end',
      });
      store.markStartTracked();

      const firstQuestion = questions[0];
      trackEvent('build_vs_buy_quiz_step_viewed', {
        step_number: 1,
        section: firstQuestion.section,
        question_id: firstQuestion.id,
      });
    }
  }, [isHydrating, store, trackEvent, variant, questions]);

  return {
    currentStep,
    answers: store.answers,
    blueCount: store.blueCount,
    orangeCount: store.orangeCount,
    resultBucket: store.resultBucket || 'toss_up',
    isTransitioning,
    isHydrating,
    isCompletingQuiz,
    handleAnswer,
    handleBack,
  };
};
